/**
 * monetization/quota — PURE quota evaluation (usage vs limits) for the UI.
 *
 * HARD enforcement lives on the server (Rust write-path + upload); this is the
 * preflight/presentation layer: usage bars, "near the limit" warnings, and
 * disabling creates when the tier is full, so the user never blindly
 * hits the server's 402/rejection.
 */

import { formatBytes } from "@fleet/utils";
import {
  QUOTA_KEYS,
  type QuotaEvaluation,
  type QuotaKey,
  type QuotaLimits,
  type UsageSnapshot,
} from "./types";

export const NEAR_LIMIT_RATIO = 0.8;

export function emptyUsage(): UsageSnapshot {
  return { storage_bytes: 0, items: 0, collections: 0, orgs: 0, attachments: 0 };
}

/** Evaluates ONE quota. */
export function evaluateQuota(
  key: QuotaKey,
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation {
  const used = usage[key] ?? 0;
  const limit = limits[key] ?? null;
  if (limit === null) {
    return { key, used, limit: null, ratio: 0, nearLimit: false, exceeded: false };
  }
  const ratio = limit <= 0 ? 1 : Math.min(used / limit, 1);
  return {
    key,
    used,
    limit,
    ratio,
    nearLimit: ratio >= NEAR_LIMIT_RATIO,
    exceeded: used >= limit,
  };
}

/** Evaluates ALL quotas (stable order of QUOTA_KEYS). */
export function evaluateQuotas(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation[] {
  return QUOTA_KEYS.map((key) => evaluateQuota(key, limits, usage));
}

/** Does an additional `delta` consumption fit within `key`? (create preflight). */
export function canConsume(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
  key: QuotaKey,
  delta = 1,
): boolean {
  const limit = limits[key] ?? null;
  if (limit === null) {
    return true;
  }
  return (usage[key] ?? 0) + delta <= limit;
}

/** Currently exceeded keys. */
export function exceededKeys(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaKey[] {
  return QUOTA_KEYS.filter((key) => evaluateQuota(key, limits, usage).exceeded);
}

/** Displayable value for a limit (`null` ⇒ "∞"). */
export function formatLimit(key: QuotaKey, limit: number | null): string {
  if (limit === null) {
    return "∞";
  }
  return key === "storage_bytes" ? formatBytes(limit) : String(limit);
}

/** Displayable value for the usage. */
export function formatUsed(key: QuotaKey, used: number): string {
  return key === "storage_bytes" ? formatBytes(used) : String(used);
}
